/* HAHAHA® — Affiche phosphorescente
   Inclinaison 3D (CSS, sans WebGL) + mode nuit + ajout au panier.
   Un seul script pour toutes les affiches : chaque section [data-hhpo-root] est autonome. */
(() => {
  if (window.__hhpoLoaded) return;
  window.__hhpoLoaded = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const MAX_Y = 38;       // inclinaison horizontale max (°)
  const MAX_X = 28;       // inclinaison verticale max (°)
  const HOVER = 6;        // légère inclinaison au survol (souris)
  const NIGHT_MS = 1500;  // durée de l'extinction
  const CHARGE_FLOOR = .55;   // le glow ne descend pas sous ce niveau
  const DECAY_TAU = 9;        // s — décroissance dans le noir
  const CHARGE_TAU = 1.1;     // s — recharge à la lumière

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const easeInOut = t => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const PAD = .3;   // marge du halo, en fraction de la largeur de l'affiche
  const hex = (v, fb) => {
    const m = /^#?([0-9a-f]{6})$/i.exec((v || '').trim());
    if (!m) return fb;
    const n = parseInt(m[1], 16);
    return [n >> 16 & 255, n >> 8 & 255, n & 255];
  };
  // flou « boîte » x3 sur le canal alpha (≈ gaussien), aller-retour horizontal + vertical
  const blur1 = (src, dst, w, h, r, horiz) => {
    const len = horiz ? w : h, lines = horiz ? h : w, step = horiz ? 1 : w, stride = horiz ? w : 1;
    const div = 2 * r + 1;
    for (let l = 0; l < lines; l++) {
      const o = l * stride;
      let acc = 0;
      for (let i = -r; i <= r; i++) acc += src[o + clamp(i, 0, len - 1) * step];
      for (let i = 0; i < len; i++) {
        dst[o + i * step] = acc / div;
        acc += src[o + Math.min(i + r + 1, len - 1) * step] - src[o + Math.max(i - r, 0) * step];
      }
    }
  };
  const render = (img, pad, sigma, rgb) => {
    const pw = 300;
    const ph = Math.round(pw * img.naturalHeight / img.naturalWidth);
    const px = Math.round(pw * pad);
    const W = pw + 2 * px, H = ph + 2 * px;
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#fff';
    ctx.fillRect(px, px, pw, ph);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(img, px, px, pw, ph);
    const data = ctx.getImageData(0, 0, W, H);          // lève une erreur si l'image n'est pas lisible
    let a = new Float32Array(W * H), b = new Float32Array(W * H);
    for (let i = 0; i < W * H; i++) a[i] = data.data[i * 4 + 3];
    const r = Math.max(1, Math.round(sigma * pw));
    for (let k = 0; k < 3; k++) { blur1(a, b, W, H, r, true); blur1(b, a, W, H, r, false); }
    for (let i = 0; i < W * H; i++) {
      data.data[i * 4] = rgb[0]; data.data[i * 4 + 1] = rgb[1]; data.data[i * 4 + 2] = rgb[2];
      data.data[i * 4 + 3] = a[i];
    }
    ctx.globalCompositeOperation = 'copy';
    ctx.putImageData(data, 0, 0);
    // URL courte (blob:) : ne gonfle jamais le style de la page
    return new Promise((ok, ko) => cv.toBlob(bl => (bl ? ok(URL.createObjectURL(bl)) : ko(new Error('blob'))), 'image/png'));
  };

  class Poster {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-hhpo-stage]');
      this.poster = root.querySelector('[data-hhpo-poster]');
      this.btn = root.querySelector('[data-hhpo-light]');
      this.label = root.querySelector('[data-hhpo-light-label]');
      if (!this.poster) return;

      this.rx = 0; this.ry = 0; this.vx = 0; this.vy = 0;
      this.tx = 0; this.ty = 0;
      this.dragging = false;
      this.n = 0; this.nFrom = 0; this.nTo = 0; this.nStart = 0;
      this.c = 1;
      this.last = 0; this.raf = 0;
      this.written = {};

      this.nightfall = document.createElement('div');
      this.nightfall.className = 'hhpo-nightfall';
      this.nightfall.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.nightfall);

      this.bindPointer();
      this.bindLight();
      this.bindKeys();
      this.bindCart();
      this.intro();
      this.bakeGlow();
    }

    /* ---------- glow pré-calculé ----------
       On dessine une seule fois le halo dans un canvas (papier moins encre, flouté),
       puis on l'affiche comme une simple image : aucun filtre CSS pendant la rotation,
       donc fluide et identique sur tous les téléphones. Si ça échoue, le CSS prend le relais. */
    bakeGlow() {
      const img = this.root.querySelector('.hhpo__ink');
      if (!img) return;
      const run = async () => {
        try {
          const cs = getComputedStyle(this.root);
          const [halo, bloom] = await Promise.all([
            render(img, PAD, .085, hex(cs.getPropertyValue('--hhpo-glow-deep'), [0, 247, 134])),
            render(img, PAD, .02, hex(cs.getPropertyValue('--hhpo-glow'), [0, 247, 134]))
          ]);
          const st = this.stage.style;
          st.setProperty('--halo-img', `url(${halo})`);
          st.setProperty('--bloom-img', `url(${bloom})`);
          st.setProperty('--glow-pad', String(PAD));
          this.root.classList.add('has-baked-glow');
        } catch (_) { /* image d'un autre domaine sans CORS : on garde la version CSS */ }
      };
      if (img.complete && img.naturalWidth) run();
      else img.addEventListener('load', run, { once: true });
    }

    /* ---------- boucle d'animation ---------- */
    kick() {
      if (!this.raf) {
        this.last = performance.now();
        this.raf = requestAnimationFrame(t => this.tick(t));
      }
    }
    tick(now) {
      const dt = Math.min(.034, (now - this.last) / 1000);
      this.last = now;

      // ressort : ferme pendant la saisie, souple au relâchement
      const k = this.dragging ? 520 : 95;
      const z = this.dragging ? 1 : .62;
      const c = 2 * Math.sqrt(k) * z;
      this.vy += (k * (this.ty - this.ry) - c * this.vy) * dt;
      this.vx += (k * (this.tx - this.rx) - c * this.vx) * dt;
      this.ry += this.vy * dt;
      this.rx += this.vx * dt;
      if (reduce.matches) { this.ry = this.ty; this.rx = this.tx; this.vx = this.vy = 0; }

      // obscurité de la pièce
      if (this.n !== this.nTo) {
        const p = reduce.matches ? 1 : clamp((now - this.nStart) / NIGHT_MS, 0, 1);
        this.n = this.nFrom + (this.nTo - this.nFrom) * easeInOut(p);
        if (p >= 1) this.n = this.nTo;
      }

      // charge phosphorescente : se recharge à la lumière, décroît doucement dans le noir
      const lit = 1 - this.n;
      const target = lit > .5 ? 1 : CHARGE_FLOOR;
      const tau = lit > .5 ? CHARGE_TAU : DECAY_TAU;
      this.c += (target - this.c) * (1 - Math.exp(-dt / tau));

      this.write();

      const moving = Math.abs(this.vx) + Math.abs(this.vy) > .02 ||
        Math.abs(this.tx - this.rx) + Math.abs(this.ty - this.ry) > .02;
      const fading = this.n !== this.nTo;
      const charging = Math.abs(target - this.c) > .003;
      if (moving || fading || charging || this.dragging) {
        this.raf = requestAnimationFrame(t => this.tick(t));
      } else {
        this.raf = 0;
      }
    }
    set(el, name, v, digits) {
      const s = v.toFixed(digits);
      if (this.written[name] === s) return;
      this.written[name] = s;
      el.style.setProperty(name, s);
    }
    write() {
      // inclinaison : seulement la scène (le panneau produit n'est jamais recalculé pendant le geste)
      this.set(this.stage, '--rx', this.rx, 2);
      this.set(this.stage, '--ry', this.ry, 2);
      this.set(this.stage, '--tilt', clamp(Math.hypot(this.rx / MAX_X, this.ry / MAX_Y), 0, 1), 2);
      // lumière : toute la section, mais à faible résolution
      this.set(this.root, '--n', this.n, 3);
      this.set(this.root, '--c', Math.round(this.c * 40) / 40, 3);   // par paliers : peu de recalculs
      const veil = this.n.toFixed(3);
      if (this.written.veil !== veil) {
        this.written.veil = veil;
        this.nightfall.style.opacity = veil;
      }
    }

    /* ---------- manipulation ---------- */
    bindPointer() {
      const p = this.poster;
      let sx = 0, sy = 0, id = null, w = 1, h = 1;

      const down = e => {
        if (e.button !== undefined && e.button !== 0) return;
        id = e.pointerId;
        const r = p.getBoundingClientRect();
        w = r.width; h = r.height;
        sx = e.clientX; sy = e.clientY;
        this.dragging = true;
        this.root.classList.add('is-touched');
        p.classList.add('is-grabbing');
        try { p.setPointerCapture(id); } catch (_) {}
        this.kick();
      };
      const move = e => {
        if (this.dragging && e.pointerId === id) {
          const dx = e.clientX - sx, dy = e.clientY - sy;
          // résistance élastique en bout de course
          this.ty = MAX_Y * Math.tanh(dx / (w * .55));
          this.tx = -MAX_X * Math.tanh(dy / (h * .45));
          this.kick();
        } else if (!this.dragging && e.pointerType === 'mouse' && finePointer.matches && !reduce.matches) {
          const r = this.stage.getBoundingClientRect();
          const nx = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1);
          const ny = clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1, 1);
          this.ty = nx * HOVER;
          this.tx = -ny * HOVER;
          this.kick();
        }
      };
      const up = e => {
        if (e.pointerId !== id) return;
        this.dragging = false;
        id = null;
        p.classList.remove('is-grabbing');
        this.tx = 0; this.ty = 0;
        this.kick();
      };

      p.addEventListener('pointerdown', down);
      p.addEventListener('pointermove', move);
      p.addEventListener('pointerup', up);
      p.addEventListener('pointercancel', up);
      p.addEventListener('lostpointercapture', up);
      this.stage.addEventListener('pointermove', e => { if (e.target !== p && !p.contains(e.target)) move(e); });
      this.stage.addEventListener('pointerleave', () => {
        if (this.dragging) return;
        this.tx = 0; this.ty = 0; this.kick();
      });
      p.addEventListener('dragstart', e => e.preventDefault());
      p.addEventListener('contextmenu', e => { if (e.pointerType !== 'mouse') e.preventDefault(); });
    }

    // petit « bonjour » à l'arrivée : montre que l'affiche bouge
    intro() {
      if (reduce.matches) return;
      const io = new IntersectionObserver(entries => {
        if (!entries.some(en => en.isIntersecting)) return;
        io.disconnect();
        setTimeout(() => {
          if (this.dragging || this.root.classList.contains('is-touched')) return;
          this.vy = -150; this.vx = 40;
          this.kick();
        }, 450);
      }, { threshold: .5 });
      io.observe(this.poster);
    }

    bindKeys() {
      this.poster.addEventListener('keydown', e => {
        const step = { ArrowLeft: [0, -16], ArrowRight: [0, 16], ArrowUp: [12, 0], ArrowDown: [-12, 0] }[e.key];
        if (!step) return;
        e.preventDefault();
        this.root.classList.add('is-touched');
        this.vx += step[0] * 6; this.vy += step[1] * 6;
        this.kick();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.nTo === 1) this.setNight(false);
      });
    }

    /* ---------- lumière ---------- */
    bindLight() {
      if (!this.btn) return;
      this.btn.addEventListener('click', () => this.setNight(this.nTo !== 1));
    }
    setNight(on) {
      this.nFrom = this.n;
      this.nTo = on ? 1 : 0;
      this.nStart = performance.now();
      this.root.classList.toggle('is-night', on);
      this.root.classList.add('is-dimming');
      this.nightfall.classList.toggle('is-on', on);
      document.documentElement.classList.toggle('hhpo-lights-off', on);
      clearTimeout(this.dimT);
      if (!on) this.dimT = setTimeout(() => this.root.classList.remove('is-dimming'), NIGHT_MS);
      if (this.btn) {
        this.btn.setAttribute('aria-pressed', String(on));
        if (this.label) this.label.textContent = on ? this.btn.dataset.onLabel : this.btn.dataset.offLabel;
        this.btn.querySelector('[data-icon="bulb"]')?.toggleAttribute('hidden', on);
        this.btn.querySelector('[data-icon="moon"]')?.toggleAttribute('hidden', !on);
      }
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (_) {} }
      this.kick();
    }

    /* ---------- panier ---------- */
    bindCart() {
      const form = this.root.querySelector('[data-hhpo-form]');
      if (!form) return;
      const add = form.querySelector('[data-hhpo-add]');
      const addLabel = form.querySelector('[data-hhpo-add-label]');
      const price = form.querySelector('[data-hhpo-price]');
      const compare = form.querySelector('[data-hhpo-compare]');
      const avail = form.querySelector('[data-hhpo-avail]');
      const msg = form.querySelector('[data-hhpo-msg]');
      let variants = [];
      try { variants = JSON.parse(this.root.querySelector('[data-hhpo-variants]')?.textContent || '[]'); } catch (_) {}
      const soon = this.root.dataset.soon === 'true';
      const mode = this.root.dataset.cartMode || 'ajax';

      const refresh = () => {
        const id = Number(new FormData(form).get('id'));
        const v = variants.find(x => x.id === id);
        if (!v) return;
        if (price) price.textContent = v.price;
        if (compare) { compare.textContent = v.compare || ''; compare.hidden = !v.compare; }
        if (!soon) {
          add.disabled = !v.available;
          addLabel.textContent = v.available ? add.dataset.labelAdd : add.dataset.labelOut;
          if (avail) {
            avail.dataset.state = v.available ? 'in' : 'out';
            avail.textContent = v.available ? avail.dataset.labelIn : avail.dataset.labelOut;
          }
        }
        if (this.root.dataset.updateUrl === 'true' && variants.length > 1) {
          const u = new URL(location.href);
          u.searchParams.set('variant', id);
          history.replaceState(history.state, '', u);
        }
      };
      form.addEventListener('change', e => { if (e.target.name === 'id') refresh(); });

      form.addEventListener('submit', async e => {
        if (mode === 'native') return;
        e.preventDefault();
        if (add.disabled) return;
        add.classList.add('is-loading');
        add.setAttribute('aria-busy', 'true');
        msg.textContent = '';

        const drawer = document.querySelector('cart-drawer');
        const notif = document.querySelector('cart-notification');
        const target = drawer || notif;
        const fd = new FormData(form);
        if (target && typeof target.getSectionsToRender === 'function') {
          fd.append('sections', target.getSectionsToRender().map(s => s.id));
          fd.append('sections_url', location.pathname);
          target.setActiveElement?.(document.activeElement);
        }
        try {
          const res = await fetch((window.routes?.cart_add_url || '/cart/add') + '.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
            body: fd
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Erreur');
          if (target && typeof target.renderContents === 'function') {
            drawer?.classList.remove('is-empty');
            target.renderContents(data);
          } else {
            location.href = window.routes?.cart_url || '/cart';
            return;
          }
          msg.textContent = msg.dataset.ok || '';
        } catch (err) {
          msg.textContent = err.message;
        } finally {
          add.classList.remove('is-loading');
          add.removeAttribute('aria-busy');
        }
      });
    }
  }

  const init = scope => {
    (scope || document).querySelectorAll('[data-hhpo-root]').forEach(el => {
      if (el.__hhpo) return;
      el.__hhpo = new Poster(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => {
    e.target.querySelectorAll('[data-hhpo-root]').forEach(el => el.__hhpo?.nightfall?.remove());
    document.documentElement.classList.remove('hhpo-lights-off');
  });
  window.HHHPoster = { init };
})();
